<template>
  <v-container fluid py-0>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-container fluid pt-0>
          <v-row align="center">
            <div>
              <h1 class="headline">Third Party API Logs</h1>
            </div>
            <v-col class="py-0">
              <list-filters
                :filters="filters"
                label="Find API Log"
                ref-name="thirdPartyApiLogsListFilters"
                @searchParamsChanged="searchParamsChanged"
                :expansionPanel="false"
                :minColumns="4"
              ></list-filters>
            </v-col>
          </v-row>

          <v-row class="mt-2">
            <v-col cols="auto">
              <v-chip outlined color="primary">
                Total: {{ totalLogsData ? totalLogsData.totalCount : "—" }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <v-chip outlined color="error">
                Failures: {{ failedLogsData ? failedLogsData.totalCount : "—" }}
              </v-chip>
            </v-col>
            <v-col cols="auto">
              <v-chip outlined color="info">
                Today: {{ todayLogsData ? todayLogsData.totalCount : "—" }}
              </v-chip>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="$apollo.loading"
      item-key="id"
      class="elevation-1"
      v-bind="pagingOptions"
      fixed-header
      hide-default-footer
      @update:options="onListOptionsUpdate"
    >
      <template #[`item.createdAt`]="{ item }">
        {{ item.createdAt }}
      </template>

      <template #[`item.operation`]="{ item }">
        <span class="mr-1">{{ item.operation }}</span>
        <v-chip x-small dark :color="httpMethodColor(item.httpMethod)">
          {{ item.httpMethod }}
        </v-chip>
      </template>

      <template #[`item.reference`]="{ item }">
        <v-btn
          v-if="item.reference"
          text
          small
          color="primary"
          @click="openItinerary(item.reference)"
        >
          {{ item.reference }}
        </v-btn>
        <span v-else>—</span>
      </template>

      <template #[`item.success`]="{ item }">
        <v-icon small :color="item.success ? 'success' : 'error'">
          {{ item.success ? "check_circle" : "cancel" }}
        </v-icon>
        <span class="ml-1">{{ item.responseCode }}</span>
      </template>

      <template #[`item.durationMs`]="{ item }">
        <span :class="item.durationMs > 5000 ? 'red--text font-weight-bold' : ''">
          {{ item.durationMs }} ms
        </span>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn icon small @click="viewDetail(item)">
          <v-icon small>visibility</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <get-more-results
      :first="first"
      @limitChange="onLimitChange"
      :items="thirdPartyApiLogs"
      :totalItems="totalItems"
      :loading="$apollo.loading"
      :loadMoreEnabled="loadMoreEnabled"
      @loadMore="loadMore('thirdPartyApiLogs')"
    ></get-more-results>

    <third-party-api-logs-detail
      :show="detailDialog"
      :log="selectedLog"
      @close="detailDialog = false"
    ></third-party-api-logs-detail>
  </v-container>
</template>

<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import {
  ALL_THIRD_PARTY_API_LOGS_QUERY,
  THIRD_PARTY_API_LOGS_TOTAL_COUNT_QUERY,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import { removeGQLTypename } from "../../../../helpers";
import ListFilters from "../../shared/list-filters/ListFilters.vue";
import ThirdPartyApiLogsDetail from "./ThirdPartyApiLogsDetail.vue";
import thirdPartyApiLogsFilters from "./third-party-api-logs-filters";

export default {
  name: "ThirdPartyApiLogsList",
  mixins: [ListMixin],
  components: {
    GetMoreResults,
    ListFilters,
    ThirdPartyApiLogsDetail,
  },

  data() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      thirdPartyApiLogs: {},
      selectedLog: null,
      detailDialog: false,
      todayIso: today.toISOString(),
      sort: { fieldName: "createdAt", direction: "DESC" },
      headers: [
        { text: "Date/Time", value: "createdAt", name: "createdAt", sortable: true },
        { text: "Operation", value: "operation", name: "operation", sortable: true },
        { text: "Reference", value: "reference", name: "reference", sortable: false },
        { text: "Status", value: "success", name: "success", sortable: true },
        { text: "Duration", value: "durationMs", name: "durationMs", sortable: true },
        { text: "Actions", value: "actions", name: "actions", sortable: false },
      ],
    };
  },

  computed: {
    items() {
      return this.thirdPartyApiLogs && this.thirdPartyApiLogs.edges
        ? this.thirdPartyApiLogs.edges.map(removeGQLTypename)
        : [];
    },
    filters() {
      return thirdPartyApiLogsFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    httpMethodColor(method) {
      const colors = { GET: "blue", POST: "orange", POST_XML: "purple" };
      return colors[method] || "grey";
    },

    openItinerary(reference) {
      this.$router.push("/itineraries?search=" + reference);
    },

    viewDetail(item) {
      this.selectedLog = item;
      this.detailDialog = true;
    },
  },

  apollo: {
    thirdPartyApiLogs: {
      query: ALL_THIRD_PARTY_API_LOGS_QUERY,
      variables() {
        return {
          after: this.after,
          sort: this.sort,
          first: this.first,
          filter: this.searchParams,
        };
      },
      fetchPolicy: "network-only",
      result({ data }) {
        this.totalItems = data.thirdPartyApiLogs.totalCount;
      },
    },
    totalLogsData: {
      query: THIRD_PARTY_API_LOGS_TOTAL_COUNT_QUERY,
      variables() {
        return { filter: {} };
      },
      fetchPolicy: "network-only",
      update(data) {
        return data.thirdPartyApiLogs;
      },
    },
    failedLogsData: {
      query: THIRD_PARTY_API_LOGS_TOTAL_COUNT_QUERY,
      variables() {
        return { filter: { success_eq: false } };
      },
      fetchPolicy: "network-only",
      update(data) {
        return data.thirdPartyApiLogs;
      },
    },
    todayLogsData: {
      query: THIRD_PARTY_API_LOGS_TOTAL_COUNT_QUERY,
      variables() {
        return { filter: { created_at_gte: this.todayIso } };
      },
      fetchPolicy: "network-only",
      update(data) {
        return data.thirdPartyApiLogs;
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/admin/logs/ThirdPartyApiLogsList.vue